// The listener rule (ADR 001), pure: for each remote audio publication, whether this
// participant plays it and whether their client keeps it subscribed at all.
//
// For a speaker in another language: in voice mode the original is muted for me and I play
// the interpreter's track vm.interp/<my language>/<speaker>; in subtitles-only and off modes I
// hear the original and no interpretation. Speech in my own language is always the original.
// Track names count only from agents: a person may name their microphone anything.

import { parseInterpretationTrackName, type ListeningMode } from './protocol';

export interface Listener {
  readonly identity: string;
  /** vm.lang of this participant. */
  readonly language: string;
  readonly mode: ListeningMode;
}

export type AudioSource = 'microphone' | 'screen_share_audio' | 'other';

export interface AudioPublication {
  readonly participantIdentity: string;
  readonly participantIsAgent: boolean;
  /** vm.lang of the publisher; null when they have none (then they are played as they are). */
  readonly participantLanguage: string | null;
  readonly trackName: string;
  readonly source: AudioSource;
}

export interface Decision {
  /** Render it audibly. */
  readonly play: boolean;
  /** Keep the subscription (false: unsubscribe, the track is never needed by this listener). */
  readonly subscribe: boolean;
  readonly reason:
    | 'own'
    | 'original'
    | 'original-muted'
    | 'interpretation'
    | 'interpretation-other-language'
    | 'interpretation-not-wanted'
    | 'agent-other';
}

export function decide(listener: Listener, publication: AudioPublication): Decision {
  if (publication.participantIdentity === listener.identity) {
    return { play: false, subscribe: false, reason: 'own' };
  }
  if (publication.participantIsAgent) {
    const track = parseInterpretationTrackName(publication.trackName);
    if (!track) return { play: false, subscribe: false, reason: 'agent-other' };
    if (track.language !== listener.language) {
      return { play: false, subscribe: false, reason: 'interpretation-other-language' };
    }
    return listener.mode === 'voice'
      ? { play: true, subscribe: true, reason: 'interpretation' }
      : { play: false, subscribe: false, reason: 'interpretation-not-wanted' };
  }
  const otherLanguage =
    publication.source === 'microphone' &&
    publication.participantLanguage !== null &&
    publication.participantLanguage !== listener.language;
  if (otherLanguage && listener.mode === 'voice') {
    // Muted, not unsubscribed: switching the mode must take effect at once.
    return { play: false, subscribe: true, reason: 'original-muted' };
  }
  return { play: true, subscribe: true, reason: 'original' };
}
